/**
 * Startup writes: data migrations and first-run seeding, expressed as events.
 *
 * An event-sourced log cannot be rewritten: the past is what was recorded, and
 * FR-9.10 replays it to answer what the catalog looked like on a given date.
 * So a migration here never edits history. It appends new facts that supersede
 * the old ones, through the same write path as any other edit -- durable before
 * the network is consulted, queued in the outbox, merged on every device.
 *
 * Each migration must therefore be idempotent by construction rather than by
 * remembering that it ran. A flag saying "done" would be a second source of
 * truth that could disagree with the data, and would be wrong on a device that
 * had never seen it. Instead each one asks the manifest whether there is still
 * anything to do, which is a question the projection can always answer.
 */
import {
  listInvestments,
  missingDefaultCategories,
  hasAnyDefaultCategory,
  type AnyEvent,
  type EntityRecord,
  type Manifest,
} from '@intentionalist/shared';
import { seedCategory, updateInvestment } from './mutations.js';

/**
 * FR-8.1: `targetPrice` became `referencePrice` on 2026-10-09.
 *
 * Entries written before then carry the old cell. The projection reads either,
 * resolved by clock, so nothing is broken without this -- but the current state
 * would stay expressed in vocabulary the product no longer uses, and every
 * future reader of the data would have to know both names.
 *
 * Restating the value under the new name fixes that for current state while
 * leaving the history intact: a replay of a date before the migration still
 * finds the old cell and still reports the same figure.
 *
 * Idempotent because of the clock test. Once the new cell is the later of the
 * two, this finds nothing to do -- on this device or any other.
 */
export function migrateReferencePrice(manifest: Manifest): AnyEvent[] {
  const events: AnyEvent[] = [];

  for (const entry of listInvestments(manifest)) {
    const record = manifest.investments.get(entry.investmentId);
    if (record === undefined || !needsRestating(record)) continue;
    // `entry.referencePrice` is already the clock-resolved value, so this writes
    // what the user currently sees rather than re-deriving it.
    if (entry.referencePrice === null) continue;
    events.push(...updateInvestment(entry.investmentId, { referencePrice: entry.referencePrice }));
  }

  return events;
}

const needsRestating = (record: EntityRecord): boolean => {
  const previous = record.fields.get('targetPrice');
  if (previous === undefined) return false;
  const current = record.fields.get('referencePrice');
  return current === undefined || current.hlc < previous.hlc;
};

/**
 * The default category set, on first run only (FR-4.1).
 *
 * The condition is "this account has never seen the defaults", not "some of them
 * are missing". The difference is the whole problem with seeding data the user
 * owns: a category they deleted is still in the manifest carrying its tombstone,
 * so it never looks missing -- but a user who deleted ten of the eleven and kept
 * one would, under a per-category test, have the other ten handed back to them
 * on the next load. Offering them once is helpful. Restoring them repeatedly is
 * the app overruling a decision the user already made.
 *
 * So the account is seeded when none of the eleven ids has ever been written,
 * and never again.
 */
export function seedDefaultCategories(manifest: Manifest): AnyEvent[] {
  if (hasAnyDefaultCategory(manifest.categories)) return [];
  return missingDefaultCategories(manifest.categories).flatMap((category) =>
    seedCategory(category.id, category.name),
  );
}

/**
 * Everything this replica owes the data on start, as one batch.
 *
 * Returns rather than writes, so the caller decides when to spend a write and
 * the whole set lands together -- which matters because a batch is one
 * transaction in the replica and one push to the server.
 *
 * Order matters once: seeding comes after migrations, so a migration never has
 * to reason about records that were not there a moment ago.
 */
export const pendingStartupWrites = (manifest: Manifest): AnyEvent[] => [
  ...migrateReferencePrice(manifest),
  ...seedDefaultCategories(manifest),
];
